import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { CertificateRequestForm } from '../../components/forms/CertificateRequestForm';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  formatCertificateRequestNotifySummary,
  notifyCertificateRequestSubmitted,
} from '../../lib/certificateRequestNotifications';
import {
  matchCertificateRequestItemsToSalesReports,
  type SalesReportMatchResult,
} from '../../lib/certificateRequestSalesReportMatch';
import {
  normalizeCertificateRequestFormData,
  patchCertificateRequestAttachments,
  submitCertificateRequest,
  uploadCertificateRequestAttachment,
  validateCertificateRequestForm,
} from '../../lib/certificateRequestSubmissions';
import { canAccessCertificateRequest } from '../../lib/applicationFormWorkflow';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import {
  EMPTY_CERTIFICATE_REQUEST_FORM,
  type CertificateRequestFormData,
} from '../../types/applicationForm';

export type CertificateRequestNewNavigateState = {
  ctrNotice?: string;
  ctrError?: string;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function CertificateRequestNewPage() {
  const { basePath } = usePortal();
  const navigate = useNavigate();
  const { profile, authUser, portalAccess, loading: authLoading } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CertificateRequestFormData>(() => ({
    ...EMPTY_CERTIFICATE_REQUEST_FORM,
    date: todayIso(),
  }));
  const [pendingCustomerFiles, setPendingCustomerFiles] = useState<File[]>([]);
  const [pendingSalesFiles, setPendingSalesFiles] = useState<File[]>([]);
  const [salesMatch, setSalesMatch] = useState<SalesReportMatchResult | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const homePath = basePath || '/forms';
  const listPath = basePath ? `${basePath}/certificate-request` : '/certificate-request';

  const allowed = canAccessCertificateRequest({
    elevatedDepartment: portalAccess?.elevatedDepartment,
    departmentSlug: portalAccess?.departmentSlug,
    formsRole: portalAccess?.forms.profile?.role,
  });

  const submitterLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  useEffect(() => {
    setForm((prev) => {
      const creator = submitterLabel.trim();
      if (!creator || prev.requested_by === creator) return prev;
      return {
        ...prev,
        requested_by: creator,
        prepared_by: '',
        verified_by: '',
      };
    });
  }, [submitterLabel]);

  useEffect(() => {
    if (pendingSalesFiles.length === 0) {
      setSalesMatch(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const result = await matchCertificateRequestItemsToSalesReports(form, pendingSalesFiles);
      if (!cancelled) setSalesMatch(result);
    })();
    return () => {
      cancelled = true;
    };
  }, [form, pendingSalesFiles]);

  if (authLoading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Checking access…</p>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to={homePath} replace />;
  }

  const handleSubmit = async () => {
    const validationError = validateCertificateRequestForm(form, {
      pendingCustomerRequirementFiles: pendingCustomerFiles,
      requireCustomerAttachments: false,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const creator = submitterLabel.trim();
      const payload = normalizeCertificateRequestFormData({
        ...form,
        date: form.date.trim() || todayIso(),
        requested_by: creator,
        prepared_by: '',
        verified_by: '',
        customer_requirement_attachments: [],
        sales_report_attachments: [],
      });
      const row = await submitCertificateRequest(
        payload,
        creator || undefined,
        authUser?.email ?? profile?.email ?? undefined,
      );

      const customerUploaded = [];
      for (const file of pendingCustomerFiles) {
        customerUploaded.push(
          await uploadCertificateRequestAttachment(row.id, 'customer_requirement', file),
        );
      }
      const salesUploaded = [];
      for (const file of pendingSalesFiles) {
        salesUploaded.push(
          await uploadCertificateRequestAttachment(row.id, 'sales_report', file),
        );
      }

      if (customerUploaded.length > 0 || salesUploaded.length > 0) {
        const withFiles = normalizeCertificateRequestFormData({
          ...(row.form_data as CertificateRequestFormData),
          customer_requirement_attachments: customerUploaded,
          sales_report_attachments: salesUploaded,
        });
        await patchCertificateRequestAttachments(row.id, withFiles);
      }

      const notifyResult = await notifyCertificateRequestSubmitted(row);
      const summary = await formatCertificateRequestNotifySummary(notifyResult);
      const state: CertificateRequestNewNavigateState = {
        ctrNotice: `Submitted. ${summary}`,
      };
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        state.ctrError =
          'Add Authorised / Approver emails in Forms settings → Certificate Request so they get notified.';
      }
      navigate(`${listPath}/${row.id}`, { replace: true, state });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="af-applications-page af-form-page">
      <header className="page-header">
        <div className="page-header__text">
          <h1 className="page-title">New Certificate Request</h1>
          <p className="page-lead page-lead--emphasis">PU-040 · Certificate Request Form</p>
          <p className="page-lead">
            Submit a request. Authorised users process it, then Approvers approve.
          </p>
        </div>
        <div className="page-header__actions">
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
        </div>
      </header>

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="card ctr-form-card">
        <CertificateRequestForm
          value={form}
          onChange={setForm}
          pendingCustomerRequirementFiles={pendingCustomerFiles}
          onPendingCustomerRequirementFilesChange={setPendingCustomerFiles}
          pendingSalesReportFiles={pendingSalesFiles}
          onPendingSalesReportFilesChange={setPendingSalesFiles}
          salesReportMatch={salesMatch}
        />
        <div className="ctr-form__actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => void handleSubmit()}
          >
            {busy ? 'Working…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
